import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, Info } from 'lucide-react'
import { isNativeApp } from '../../utils/nativeApp.js'
import MediaCaptureField from '../common/MediaCaptureField.js'

const MAX_PHOTOS = 10
const MAX_VIDEOS = 5
const HANDHELD_MQ = '(max-width: 1366px) and (pointer: coarse), (max-width: 1024px)'

const isVideoFile = (file) =>
  file?.type?.startsWith('video/') || /\.(mp4|webm|mov|m4v|3gp)$/i.test(file?.name || '')

/**
 * Multi photo/video picker for booking.
 * files: File[] (new uploads)
 * existing: [{id, url/cdn_url, media_type}] from API
 * removedIds: number[] of existing media to delete on save
 */
const ShipmentMediaUploader = ({
  files = [],
  onFilesChange,
  existing = [],
  removedIds = [],
  onRemovedIdsChange,
  disabled = false,
  dropzone = false,
}) => {
  const [handheld, setHandheld] = useState(() => (
    typeof window !== 'undefined' && (isNativeApp() || window.matchMedia(HANDHELD_MQ).matches)
  ))
  const [viewer, setViewer] = useState(null)

  useEffect(() => {
    const mq = window.matchMedia(HANDHELD_MQ)
    const sync = () => setHandheld(isNativeApp() || mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  const visibleExisting = useMemo(
    () => (existing || []).filter((m) => m?.id == null || !removedIds.includes(m.id)),
    [existing, removedIds],
  )

  const photoCount =
    visibleExisting.filter((m) => (m.media_type || 'photo') === 'photo').length +
    files.filter((f) => !isVideoFile(f)).length
  const videoCount =
    visibleExisting.filter((m) => m.media_type === 'video').length +
    files.filter((f) => isVideoFile(f)).length

  const removeNewFile = (index) => {
    onFilesChange?.(files.filter((_, i) => i !== index))
  }

  const removeExisting = (id) => {
    if (id == null) return
    onRemovedIdsChange?.([...removedIds, id])
  }

  const previewGrid = (visibleExisting.length > 0 || files.length > 0) && (
    <div className={dropzone || handheld ? 'grid grid-cols-3 gap-2' : 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2'}>
      {visibleExisting.map((item, idx) => {
        const url = item.cdn_url || item.url
        const video = item.media_type === 'video'
        return (
          <div
            key={`ex-${item.id ?? 'legacy'}-${idx}`}
            className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-gray-50"
          >
            <button
              type="button"
              className="block h-full w-full"
              onClick={() => url && setViewer({ url, video })}
              aria-label="View photo"
            >
              {video ? (
                <video src={url} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                removeExisting(item.id)
              }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            {video && (
              <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
                Video
              </span>
            )}
          </div>
        )
      })}
      {files.map((file, index) => {
        const url = URL.createObjectURL(file)
        const video = isVideoFile(file)
        return (
          <div
            key={`new-${index}-${file.name}`}
            className="relative aspect-square rounded-xl overflow-hidden border border-orange-300 bg-orange-50"
          >
            <button
              type="button"
              className="block h-full w-full"
              onClick={() => setViewer({ url, video })}
              aria-label="View photo"
            >
              {video ? (
                <video src={url} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                removeNewFile(index)
              }}
              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
              aria-label="Remove"
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1.5 py-0.5 rounded">
              New{video ? ' video' : ''}
            </span>
          </div>
        )
      })}
    </div>
  )

    return (
    <div className="space-y-2">

      {/* Take Photo, Upload and Info Tooltip */}
      <div className="flex items-center gap-2 flex-wrap">

        <MediaCaptureField
          mode="media"
          disabled={disabled}
          photoCount={photoCount}
          videoCount={videoCount}
          maxPhotos={MAX_PHOTOS}
          maxVideos={MAX_VIDEOS}
          onFiles={(picked) => onFilesChange?.([...files, ...picked])}
        />

        {/* Info Tooltip */}
        <div className="relative group inline-flex items-center">

          <button
            type="button"
            aria-label="Media upload information"
            className="flex items-center justify-center text-gray-500 hover:text-blue-600 transition-colors"
          >
            <Info size={17} />
          </button>

          {/* Tooltip Content */}
          <div
            role="tooltip"
            className="
              absolute
              bottom-full
              left-1/2
              -translate-x-1/2
              mb-2
              w-72
              max-w-[calc(100vw-2rem)]
              rounded-lg
              bg-gray-900
              px-3
              py-2
              text-xs
              leading-5
              text-white
              shadow-lg
              opacity-0
              invisible
              group-hover:opacity-100
              group-hover:visible
              group-focus-within:opacity-100
              group-focus-within:visible
              transition-all
              duration-200
              z-50
              pointer-events-none
            "
          >
            Up to {MAX_PHOTOS} photos (5MB) and {MAX_VIDEOS} videos (50MB).
            JPG, JPEG, PNG, and video.

            {/* Tooltip Arrow */}
            <div
              className="
                absolute
                top-full
                left-1/2
                -translate-x-1/2
                border-[6px]
                border-transparent
                border-t-gray-900
              "
            />
          </div>

        </div>

      </div>

      {/* Existing Photo and Video Preview */}
      {previewGrid}

      {viewer?.url ? createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setViewer(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-3 shadow-lg"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Image preview"
          >
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-800">Preview</p>
              <button
                type="button"
                onClick={() => setViewer(null)}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100"
                aria-label="Close preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {viewer.video ? (
              <video src={viewer.url} controls playsInline className="max-h-80 w-full rounded-lg bg-black" />
            ) : (
              <img src={viewer.url} alt="Selected upload" className="max-h-80 w-full rounded-lg bg-gray-50 object-contain" />
            )}
          </div>
        </div>,
        document.body,
      ) : null}

    </div>
  )
}

export default ShipmentMediaUploader
